import { useForm } from "react-hook-form";
import { Form, FormField, FormItem, FormControl } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, Flag } from "lucide-react";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface CommentForm {
  content: string;
}

interface Comment {
  id: number;
  content: string;
  created_at: string;
  user_id: string;
}

interface Props {
  requestId: number;
  comments: Comment[];
  isAdmin: boolean;
  onCommentAdded: () => void;
}

export function PrayerComments({ requestId, comments, isAdmin, onCommentAdded }: Props) {
  const session = useSession();
  const { toast } = useToast();
  const commentForm = useForm<CommentForm>();

  const handleComment = async (data: CommentForm) => {
    if (!session) return;

    try {
      const { error } = await supabase
        .from('prayer_comments')
        .insert([
          {
            content: data.content,
            prayer_request_id: requestId,
            user_id: session.user.id,
          }
        ]);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to submit comment. Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Your comment has been added.",
      });
      commentForm.reset();
      onCommentAdded();
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const { error } = await supabase
        .from('prayer_comments')
        .delete()
        .eq('id', commentId);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to delete comment.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Comment deleted successfully.",
      });
      onCommentAdded();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleReport = async (commentId: number) => {
    if (!session) {
      toast({
        title: "Sign in required",
        description: "Please sign in to report content",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Report Submitted",
      description: "Thank you for helping keep our community safe.",
    });
  };

  return (
    <div className="mt-6">
      <h4 className="font-semibold mb-4">Comments</h4>
      <ScrollArea className="h-[300px] mb-4 pr-4">
        {comments && comments.length > 0 ? (
          comments.map((comment) => (
            <Card key={comment.id} className="p-4 mb-3 relative">
              <p className="text-sm mb-2">{comment.content}</p>
              <div className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  {formatDate(comment.created_at)}
                </p>
                <div className="flex items-center gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-muted-foreground hover:text-red-500"
                            >
                              <Flag className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Report Comment</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to report this comment? This will notify our moderators to review the content.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleReport(comment.id)}>
                                Report
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Report this</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))
        ) : (
          <p className="text-muted-foreground text-center">No comments yet</p>
        )}
      </ScrollArea>
      <Form {...commentForm}>
        <form onSubmit={commentForm.handleSubmit(handleComment)} className="space-y-4">
          <FormField
            control={commentForm.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Add your comment..."
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full">
            Add Comment
          </Button>
        </form>
      </Form>
    </div>
  );
}
